import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';
import { ReactNode } from 'react';
import { ModifiedFormControlProps } from '../../types/ModifiedFormControlProps';

/**
 * The properties for the formik checkbox
 */
type FormikCheckboxProps<T extends object, K extends keyof T & string> = {
  /**
   * The formik object that's connected to the form that the checkbox will be within
   */
  formik: FormikState<T> & FormikHandlers & FormikHelpers<T>;
  /**
   * The name of the field that's associated with this checkbox
   */
  name: K;
  /**
   * (Optional) A label to show with the checkbox
   */
  label?: ReactNode;

  /**
   * (Optional) This is optional helper text that can show to provide additional guidance
   * to the user
   */
  helpText?: string;

  /**
   * (Optional) Whether the form field is disabled. The field will always disable while
   *  the form is being submitted, regardless of this value.
   */
  disabled?: boolean;
} & ModifiedFormControlProps;

/**
 * The formik checkbox is a component that should be used within a formik form. It makes
 *  setting up the form more standard and less prone to error.
 */
const FormikCheckbox = <T extends object, K extends keyof T & string>(
  props: FormikCheckboxProps<T, K>
) => {
  return (
    <FormControl
      id={props.name}
      disabled={props.formik.isSubmitting || props.disabled}
      error={
        props.formik.touched[props.name] &&
        Boolean(props.formik.errors[props.name])
      }
      {...props}
    >
      <FormControlLabel
        control={
          <Checkbox
            id={`${props.name}-checkbox`}
            name={props.name}
            checked={props.formik.values[props.name] as boolean}
            onChange={(e) => {
              props.formik.setFieldTouched(props.name);
              props.formik.handleChange(e);
            }}
          />
        }
        label={props.label ?? <></>}
      />
      <FormHelperText
        sx={{
          marginTop: '-.8em',
          marginBottom: '1.5em',
        }}
      >
        {((props.formik.touched[props.name] &&
          props.formik.errors[props.name]) as string) || props.helpText}
      </FormHelperText>
    </FormControl>
  );
};

export default FormikCheckbox;
